import { Link } from "react-router-dom";
import {
  Info,
  MessageSquare,
  Megaphone,
  ImageIcon,
  Layers,
  Film,
  Mic,
  Clapperboard,
  PlaySquare,
  Code2,
  Sparkles,
  Cpu,
  Globe,
  ArrowRight,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import Logo from "../components/Logo.jsx";
import { useI18n } from "../lib/i18n.jsx";

const TOOLS = [
  {
    to: "/chat",
    icon: MessageSquare,
    title: "AI Chat",
    desc: "Streamed conversations with easy copy-to-clipboard actions on every message.",
  },
  {
    to: "/marketing",
    icon: Megaphone,
    title: "Marketing",
    desc: "Generate social post copy plus image creatives with built-in copy/download actions.",
  },
  {
    to: "/image",
    icon: ImageIcon,
    title: "Image Generation",
    desc: "NVIDIA, Together, and Pollinations image generation in one workspace.",
  },
  {
    to: "/image-studio",
    icon: Layers,
    title: "Image Studio",
    desc: "Layer images and text overlays, then export final composites as PNG.",
  },
  {
    to: "/video",
    icon: Film,
    title: "Video Generation",
    desc: "Video generation is currently being rebuilt.",
    comingSoon: true,
  },
  {
    to: "/tts",
    icon: Mic,
    title: "Text to Speech",
    desc: "Natural neural voices for any script or narration.",
  },
  {
    to: "/studio",
    icon: Clapperboard,
    title: "Video Studio",
    desc: "Combine scenes, captions, audio, and backgrounds.",
  },
  {
    to: "/shorts",
    icon: PlaySquare,
    title: "Shorts Studio",
    desc: "Upload your own clip and auto-generate vertical shorts with transcript-driven clip picks and MP4 export.",
  },
  {
    to: "/code",
    icon: Code2,
    title: "Code Editor",
    desc: "Build React web and React Native apps with live preview.",
  },
];

const VALUES = [
  {
    icon: Sparkles,
    title: "100% free and unlimited",
    desc: "No sign-in and no subscription needed. Open any tool and create right away.",
  },
  {
    icon: Cpu,
    title: "Powered by NVIDIA NIM",
    desc: "Models run through NVIDIA's inference stack for fast, reliable chat, vision, speech, and code.",
  },
  {
    icon: Globe,
    title: "Built for makers",
    desc: "From quick experiments to deployable projects — download your code and take it anywhere.",
  },
];

export default function About() {
  const { t: tr } = useI18n();

  return (
    <div className="flex flex-col min-h-full">
      <PageHeader
        icon={Info}
        title="About"
        subtitle={tr(
          "about.subtitle",
          "What The Consensus A.I. is and what you can do here."
        )}
      />

      <div className="px-6 md:px-10 py-10 md:py-14 max-w-4xl mx-auto w-full space-y-14">
        <section className="relative">
          <div className="absolute inset-0 -z-10 bg-grid-fade rounded-3xl" />
          <div className="card !p-8 md:!p-10 text-center">
            <Logo className="h-20 w-20 mx-auto drop-shadow-[0_0_32px_rgba(58,91,255,0.45)]" />
            <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">
              The Consensus{" "}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                A.I.
              </span>
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed max-w-2xl mx-auto">
              {tr(
                "about.paragraph",
                "The Consensus A.I. is a free, all-in-one AI studio on the web. Today you can use Chat, Marketing, Image Generation, Image Studio, Text to Speech, Video Studio, Shorts Studio, and the Code Editor with no sign-up required. Video Generation is currently being rebuilt and marked coming soon."
              )}
            </p>
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold mb-4">
            {tr("about.values.title", "What we believe")}
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {VALUES.map((v) => (
              <div key={v.title} className="card !p-5">
                <div className="h-10 w-10 rounded-lg bg-consensus-500/10 border border-consensus-500/20 flex items-center justify-center mb-3">
                  <v.icon className="h-5 w-5 text-consensus-300" />
                </div>
                <h4 className="font-semibold">
                  {v.title === "100% free and unlimited"
                    ? tr("about.values.free.title", v.title)
                    : v.title === "Powered by NVIDIA NIM"
                    ? tr("about.values.powered.title", v.title)
                    : tr("about.values.makers.title", v.title)}
                </h4>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">
                  {v.title === "100% free and unlimited"
                    ? tr("about.values.free.desc", v.desc)
                    : v.title === "Powered by NVIDIA NIM"
                    ? tr("about.values.powered.desc", v.desc)
                    : tr("about.values.makers.desc", v.desc)}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold mb-4">
            {tr("about.tools.title", "Tools in the studio")}
          </h3>
          <div className="mb-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/70 leading-relaxed">
            {tr(
              "about.shortsUploadNote",
              "For Shorts Studio: download YouTube videos on your own, upload clips directly, and let AI generate short variants with captions and smart framing."
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {TOOLS.map((tool) => (
              <Link
                key={tool.to}
                to={tool.to}
                className="group card !p-4 hover:border-white/20 transition flex items-start gap-3"
              >
                <div className="h-9 w-9 shrink-0 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <tool.icon className="h-4 w-4 text-white/80" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">
                      {tool.title === "AI Chat"
                        ? tr("home.feature.chat.title", tool.title)
                        : tool.title === "Marketing"
                        ? tr("home.feature.marketing.title", tool.title)
                        : tool.title === "Image Generation"
                        ? tr("home.feature.image.title", tool.title)
                        : tool.title === "Image Studio"
                        ? tr("home.feature.imageStudio.title", tool.title)
                        : tool.title === "Video Generation"
                        ? tr("home.feature.video.title", tool.title)
                        : tool.title === "Text to Speech"
                        ? tr("home.feature.tts.title", tool.title)
                        : tool.title === "Video Studio"
                        ? tr("home.feature.studio.title", tool.title)
                        : tool.title === "Shorts Studio"
                        ? tr("home.feature.shorts.title", tool.title)
                        : tr("home.feature.code.title", tool.title)}
                    </span>
                    {tool.comingSoon && (
                      <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200/90 border border-amber-500/20">
                        {tr("common.soon", "Soon")}
                      </span>
                    )}
                    <ArrowRight className="h-3.5 w-3.5 text-white/30 group-hover:text-accent-400 group-hover:translate-x-0.5 transition" />
                  </div>
                  <p className="text-sm text-white/55 mt-0.5">{tool.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="card !p-6 md:!p-8 border-consensus-500/20 bg-consensus-500/5">
          <h3 className="text-lg font-semibold">
            {tr("about.ready.title", "Ready to create?")}
          </h3>
          <p className="text-white/65 mt-2 text-sm leading-relaxed max-w-xl">
            {tr(
              "about.ready.desc",
              "Head to the home page to pick a tool, or jump straight into Chat, Shorts Studio, Marketing, Image Generation, or the Code Editor. Everything runs in your browser — no account required."
            )}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/" className="btn-primary">
              {tr("about.ready.home", "Go to Home")}
            </Link>
            <Link to="/chat" className="btn-secondary">
              {tr("about.ready.chat", "Start chatting")}
            </Link>
          </div>
        </section>

        <p className="text-center text-xs text-white/35 pb-4">
          theconsensusai.com · Built with React &amp; NVIDIA NIM
        </p>
      </div>
    </div>
  );
}
